import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DocumentMediaModel,
  OfflineService,
  PhdsoftCommonService,
  AssetInfoModel,
  SESSION_STORAGE,
  StorageService,
  indexedDbStore,
  Util,
  User,
} from '@phdsoft/common';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CurrentAsset } from '../models/current-asset';
import { map, shareReplay } from 'rxjs/operators';
import { RegionsService } from 'src/app/admin/services/regions.service';
import { SectionsService } from 'src/app/admin/services/sections.service';
import { AssetsCacheService } from './asset-cache.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class AssetService {
  public fullAsset: any = [];
  public assetId: string;
  public asset: CurrentAsset;
  public elmtFilter: boolean = false;
  public reportModal: boolean = false;

  public constructor(
    private readonly storageService: StorageService,
    public readonly phdsoftCommonService: PhdsoftCommonService,
    private readonly offlineService: OfflineService,
    private readonly regionsService: RegionsService,
    private readonly sectionsService: SectionsService,
    private readonly http: HttpClient,
    private readonly assetsCacheService: AssetsCacheService
  ) {

  }

  async getAssetInfobyId(assetId: string, saveOffline: boolean = false): Promise<CurrentAsset> {
    let _RESPONSE: CurrentAsset = null;
    const online = window.navigator.onLine;
    //   const assetInfoModel: CurrentAsset = await this.assetOffline(assetId);

    //   if (!Util.isNullOrUndefined(assetInfoModel)) {
    //     _RESPONSE = assetInfoModel;
    //   }
    if (online && Util.isNullOrUndefined(_RESPONSE)) {
      _RESPONSE = await this.getFromMicroservice(assetId);
      if (saveOffline) {
        await this.offlineService.storeAsync(_RESPONSE.AssetInfo.AssetId, _RESPONSE)
      }
    }
    else {
      const response = await this.offlineService.getItem(assetId);
      _RESPONSE = response ? response.json : undefined;
    }

    if (!Util.isNullOrUndefined(_RESPONSE)) {
      _RESPONSE?.AssetInfo?.Regions?.forEach(async (region) => {
        const offlineId = `${_RESPONSE.AssetInfo.AssetId}_##_${region.id}`;
        const offlineInfo = await this.offlineService.getItem(offlineId);
        if (!Util.isNullOrUndefined(offlineInfo)) {
          region.offline = offlineInfo.json as boolean;
          region.selected = false;
        }
      });

      this.assetId = _RESPONSE.AssetId;
      this.asset = _RESPONSE;
      if (Util.isNullOrUndefined(this.asset.AssetInfo)) {
        this.asset.AssetInfo = _RESPONSE.AssetInfo;
      }
    }

    return _RESPONSE;
  }

  // TODO: name suggests it gets from backend, but implementation gets from in-memory cache. Fix or implement new method
  async getFromMicroservice(assetId: string): Promise<CurrentAsset> {
    let asset$ = this.assetsCacheService.getValue(assetId)
    if (!asset$) {
      const url = 'AssetInfo/secureurl/' + assetId;
      asset$ = this.http.get<any>(
        environment.API_THREED + url
      ).pipe(
        shareReplay(1)
      );
      this.assetsCacheService.setValue(asset$, assetId)
    }

    // Clone the cached value to ensure immutability for each subscriber
    const clonedAsset$ = asset$.pipe(
      map(data => JSON.parse(JSON.stringify(data))) // Clone for each subscription
    );

    let currentAssetInfo: CurrentAsset = null;
    if (!Util.isNullOrUndefined(clonedAsset$)) {
      currentAssetInfo = {
        AssetId: assetId,
        AssetInfo: await clonedAsset$.toPromise() as AssetInfoModel,
        AssetMapInfo: {},
        AssetDocInfo: [],
      };
    }
    currentAssetInfo.AssetInfo.Regions = await this.regionsService.getRegions(assetId).toPromise();
    currentAssetInfo.AssetInfo.Sections = await this.sectionsService.getSections(assetId).toPromise();

    return currentAssetInfo;
  }

  async getFromMicroserviceOnline(assetId: string): Promise<CurrentAsset> {
    let asset$ = this.assetsCacheService.getValue(assetId)
    {
      const url = 'AssetInfo/secureurl/' + assetId;
      asset$ = this.http.get<any>(
        environment.API_THREED + url
      ).pipe(shareReplay(1));
      //this.assetsCacheService.setValue(asset$, assetId)
    }

    let currentAssetInfo: CurrentAsset = null;
    if (!Util.isNullOrUndefined(asset$)) {
      currentAssetInfo = {
        AssetId: assetId,
        AssetInfo: await asset$.toPromise() as AssetInfoModel,
        AssetMapInfo: {},
        AssetDocInfo: [],
      };
    }

    return currentAssetInfo;
  }

  async checkModelVersion(assetId: string) {
    return this.http.get<AssetInfoModel>(
      environment.API_THREED + 'AssetInfo/CheckModelVersion/' + assetId
    ).toPromise();
  }

  getUserAssetsLiteRequest(userAssets: string[]): Observable<AssetInfoModel[]> {
    return this.http.post<AssetInfoModel[]>(environment.API_THREED + 'AssetInfo/GetAssetsLite', userAssets)
      .pipe(
        shareReplay(1)
      );
  }

  GetStatusAsset(assetId: string) {
    return this.http.get<AssetInfoModel>(
      environment.API_THREED + 'AssetInfo/secureurl/GetStatusAsset/' + assetId
    );
  }

  // Zip of the stored geometry and property JSON of the given regions (Geometries/ and Properties/
  // folders, one file per region). An empty regionIds array means every region on the server, so
  // callers must not send one unless that is intended. 404 when none of the regions has stored files.
  downloadRegionsGeometriesAndProperties(assetId: string, regionIds: string[]): Observable<HttpResponse<Blob>> {
    const url = environment.API_THREED + `AssetInfo/downloadGeometriesAndProperties?assetId=${encodeURIComponent(assetId)}`
    return this.http.post(url, regionIds, { responseType: 'blob', observe: 'response' })
  }

  elementFilterQrCode(x: boolean) {
    this.elmtFilter = x
  }

  reportModalQrCode(x: boolean) {
    this.reportModal = x
  }

  async getUserAssets(): Promise<AssetInfoModel[]> {
    const user: User = await this.storageService.getItem(SESSION_STORAGE.USER)
    let userAssets: string[] = []
    user.AssetPermissions.forEach(item => {
      userAssets.push(item.AssetId)
    })
    let userAssetsInfo = await this.storageService.getItem(SESSION_STORAGE.USER_ASSETS, true) as AssetInfoModel[]
    if (!userAssetsInfo) {
      userAssetsInfo = await this.getUserAssetsLiteRequest(userAssets).toPromise()
      // save assets to offline cache
      await this.storageService.storeAsync(SESSION_STORAGE.USER_ASSETS, userAssetsInfo, true);
    }

    return userAssetsInfo;
  }

  async getCurrentAsset(assetId?: string): Promise<CurrentAsset> {
    const online = window.navigator.onLine;
    //const online = true;

    if (online && assetId) {
      this.asset = await this.getFromMicroservice(assetId);
      this.assetId = this.asset.AssetId;
      return this.asset;
    }

    let returnAsset: CurrentAsset = null;
    let _ASSET: any = sessionStorage.getItem(SESSION_STORAGE.CURRENT_ASSET);

    if (Util.isNullOrUndefined(_ASSET)) {
      const offline: indexedDbStore = await this.offlineService.getItem(
        SESSION_STORAGE.CURRENT_ASSET,
      );

      if (!Util.isNullOrUndefined(offline)) {
        _ASSET = offline.json;
      }
    }

    if (!Util.isNullOrUndefined(_ASSET)) {
      if (Util.isJson(_ASSET)) {
        returnAsset = JSON.parse(_ASSET);
      } else {
        returnAsset = JSON.parse(JSON.stringify(_ASSET));
      }

      // if (online && assetId) {
      //   const collections = await this.getFromMicroservice(assetId);
      //   returnAsset.AssetInfo.Collections = collections.AssetInfo.Collections;
      // }

      _ASSET = null;
      if (returnAsset.AssetInfo) {
        this.asset = returnAsset;
        this.assetId = this.asset.AssetId;
      }
      else {
        this.assetId = returnAsset.AssetId;
      }
    }
    else {
      if (online && assetId) {
        this.asset = await this.getFromMicroservice(assetId);
        this.assetId = this.asset.AssetId;
      }
    }

    returnAsset = null;

    return this.asset;
  }

  async saveCurrentAsset(
    dataValue: CurrentAsset,
    documentList?: DocumentMediaModel[],
  ): Promise<void> {
    this.asset = dataValue;
    if (Util.isNullOrUndefined(this.asset.AssetDocInfo)) {
      this.asset.AssetDocInfo = documentList;
    }

    this.assetId = dataValue.AssetId;

    // Store current asset info on Database
    sessionStorage.setItem(SESSION_STORAGE.CURRENT_ASSET, JSON.stringify(dataValue));

    await this.offlineService.storeAsync(SESSION_STORAGE.CURRENT_ASSET, dataValue);
    return Promise.resolve(void 0);
  }

  async deleteAsset(assetId: string) {
    const url = 'AssetInfo?pAssetId=' + assetId;
    const assetInfo = await this.http.delete(
      environment.API_THREED + url
    ).toPromise();
  }

  async getAllAssets(complete: boolean) {
    const url = 'AssetInfo/GetAll?complete=' + complete;
    return this.http.get<AssetInfoModel[]>(
      environment.API_THREED + url
    ).toPromise();
  }

  async UpdateAsset(asset: AssetInfoModel) {
    this.updateAssetInStorage(asset)

    const url = 'AssetInfo';
    this.http.put(
      environment.API_THREED + url,
      asset
    ).toPromise();

    this.resetAssetInCache(asset.AssetId);
  }

  AddAsset(asset: AssetInfoModel) {
    const url = 'AssetInfo/AddAssetInfo/';
    return this.http.post(
      environment.API_THREED + url,
      asset,
      { responseType: 'text' }
    );
  }

  VeryShallowCloneAssetInfo(asset: AssetInfoModel) {
    const url = 'AssetInfo/VeryShallowCloneAssetInfo/';
    return this.http.post(
      environment.API_THREED + url,
      asset,
      { responseType: 'text' }
    )
  }

  CloneAssetInfo(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post<boolean>(`${environment.API_THREED}AssetInfo/CloneAssetInfo`, null, { params: params })
  }

  cloneInspections(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post(`${environment.API_INSPEC}Inspection/CloneInspections`, null, { params: params })
  }

  cloneClusters(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post<boolean>(`${environment.API_INSPEC}Cluster/CloneClusters`, null, { params: params })
  }

  cloneFilters(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post<boolean>(`${environment.API_INSPEC}Filter/CloneFilters`, null, { params: params })
  }

  clonePlans(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post<boolean>(`${environment.API_INSPEC}InspectionPlan/ClonePlans`, null, { params: params })
  }

  cloneInspectionsAndPlans(srcAssetId: string, destAssetId: string, cloneAnomalies: boolean = false) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)
      .set('cloneAnomalies', cloneAnomalies.toString())

    return this.http.post(`${environment.API_INSPEC}Inspection/CloneInspectionsAndPlans`, null, { params: params })
  }

  async assetOffline(assetId: string): Promise<CurrentAsset> {
    const offline: indexedDbStore = await this.offlineService.getItem(assetId);

    // If offline get offline resources
    if (!Util.isNullOrUndefined(offline)) {
      this.asset = Util.isJson(offline.json) ? JSON.parse(offline.json) : offline.json;
      this.assetId = this.asset.AssetId;
    }

    return this.asset;
  }

  async cloneAssetElements(srcAssetId: string, destAssetId: string) {
    return this.http.post<boolean>(`${environment.API_THREED}ElementTypes/CloneAssetElements?originAssetId=${srcAssetId}&targetAssetId=${destAssetId}`, {}).toPromise();
  }

  cloneRiskShieldConfiguration(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post(`${environment.API_INSPEC}RiskShield/CloneRiskShieldConfiguration`, null, { params: params })
  }

  cloneConsequenceMaps(srcAssetId: string, destAssetId: string) {
    const params = new HttpParams()
      .set('srcAssetId', srcAssetId)
      .set('destAssetId', destAssetId)

    return this.http.post(`${environment.API_INSPEC}ConsequenceMap/CloneConsequenceMaps`, null, { params: params })
  }

  async deleteAssetElements(assetId: string) {
    return this.http.delete<boolean>(`${environment.API_THREED}ElementTypes/DeleteAssetElements?assetId=${assetId}`, {}).toPromise();
  }

  public resetAssetInCache(assetId: string) {
    this.assetsCacheService.setValue(null, assetId);
  }

  /** Regions/sections are cached per asset; call after creating or re-linking them. */
  public clearRegionsAndSectionsCache(): void {
    this.regionsService.clearCache();
    this.sectionsService.clearCache();
  }

  async updateAssetInfoCollectionsInCache(assetId: string, regionId: string) {
    const url = 'AssetInfo/secureurl/' + assetId;
    let currentAsset = await this.getCurrentAsset(assetId)

    let currentAssetCollections = currentAsset.AssetInfo.Collections[0]
    let regionIdBlobPath = currentAssetCollections.BlobPaths.find(x => x.RegionId == regionId)

    // A region emptied by a recompartment has null paths in the collection; treat it as stale too.
    if (!regionIdBlobPath?.PropertiesPath?.includes(assetId)) {
      let freshAssetInfo = await this.http.get<AssetInfoModel>(
        environment.API_THREED + url
      ).toPromise()

      currentAsset.AssetInfo.Collections = freshAssetInfo.Collections

      this.saveCurrentAsset(currentAsset)
    }
  }
  async updateAssetInStorage(asset: AssetInfoModel) {
    let tempAssetsList = await this.getUserAssets();

    if (!Util.isNullOrUndefined(tempAssetsList)) {
      const index = tempAssetsList.findIndex(item => item.AssetId === asset.AssetId);
      if (index !== -1) {
        tempAssetsList[index] = asset;
        await this.storageService.storeAsync(SESSION_STORAGE.USER_ASSETS, tempAssetsList);
      }
    }
  }

  async getUserAssetsOnline(): Promise<AssetInfoModel[]> {
    const user: User = await this.storageService.getItem(SESSION_STORAGE.USER)
    let userAssets: string[] = []
    user.AssetPermissions.forEach(item => {
      userAssets.push(item.AssetId)
    })
    let userAssetsInfo = await this.getUserAssetsLiteRequest(userAssets).toPromise()
    // save assets to offline cache
    await this.storageService.storeAsync(SESSION_STORAGE.USER_ASSETS, userAssetsInfo, true);
    return userAssetsInfo;
  }

  getInvalidElements(assetId: string) {
    return this.http.get(`${environment.API_THREED}AssetInfo/GetEmptyPropertyElements/${assetId}`);
  }

  async getFromBackEnd(assetId: string): Promise<AssetInfoModel> {

    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    const uniqueId = uuidv4();
    let params = new HttpParams().set('uuid', uniqueId);

    const url = 'AssetInfo/secureurl/' + assetId;
    let asset$ = this.http.get<any>(
      environment.API_THREED + url, { params: params, headers: headers }
    );
    this.assetsCacheService.setValue(asset$, assetId)

    let currentAssetInfo: CurrentAsset = null;
    if (!Util.isNullOrUndefined(asset$)) {
      currentAssetInfo = {
        AssetId: assetId,
        AssetInfo: await asset$.toPromise() as AssetInfoModel,
        AssetMapInfo: {},
        AssetDocInfo: [],
      };
    }
    currentAssetInfo.AssetInfo.Regions = await this.regionsService.getRegions(assetId).toPromise();
    currentAssetInfo.AssetInfo.Sections = await this.sectionsService.getSections(assetId).toPromise();

    return currentAssetInfo.AssetInfo;
  }
  getInspectionListByCustomerId(customerId: string) {
    return this.http.get<string[]>(`${environment.API_THREED}AssetInfo/GetInspectionListByCustomerId?customerId=${customerId}`)
  }


}
